import type {
  ArtworkController, ArtworkMode, ArtworkModeChangeResult, ArtworkTarget,
} from "./artwork-controller";
import type {
  ConfiguratorController, ConfiguratorEffect, ConfiguratorField,
  ConfiguratorSchema, DimensionField,
} from "./configurator-controller";
import type { DraftController, DraftOrderContext, PvcDraftSourceSnapshot } from "./draft-controller";
import type {
  CartSessionRecoveryOutcome, CartSnapshot, OrderLineController, OrderMutationConflict, OrderMutationFailure,
  PvcOrderSubmission,
} from "./order-line-controller";
import type {
  Clock, PricingController, PricingQuoteOutcome, PvcConfiguration, Scheduler,
} from "./pricing-controller";

export type DebouncedField = DimensionField | "quantity";

/* ============================================================
   Submit contract
   ============================================================ */

export type SubmitBlockedReason =
  | { kind: "configuration"; errorFields: ConfiguratorField[] }
  | { kind: "artwork"; reason: "MODE_REQUIRED" | "FILE_REQUIRED" | "UPLOAD_PROCESSING" }
  | { kind: "quote"; reason: "NOT_READY" | "STALE" }
  | { kind: "cart-ownership" }
  | { kind: "price-changed" }
  | { kind: "busy" };

export type SubmitOutcome =
  | { type: "success"; orderLineId: string; orderLineVersion: number; cart: CartSnapshot }
  | { type: "blocked"; reason: SubmitBlockedReason }
  | { type: "conflict"; conflict: OrderMutationConflict }
  | { type: "unknown-outcome"; clientMutationId: string }
  | { type: "pending"; clientMutationId: string }
  | { type: "failed"; failure: OrderMutationFailure };

/* ============================================================
   Init contract
   ============================================================ */

export type InitOutcome = {
  draft: "none" | "restored" | "discarded";
  removedAddonIds: string[];
  materialInvalidated: boolean;
};

export interface ProductPageController {
  /** Restores a draft (if any), reconciles it against the schema and quotes. */
  init(): Promise<InitOutcome>;

  onDimensionInput(field: DimensionField, raw: string): void;
  onDimensionBlur(field: DimensionField): Promise<void>;
  onQuantityInput(raw: string): void;
  onQuantityBlur(): Promise<void>;
  onQuantityStep(delta: -1 | 1): Promise<void>;
  onMaterialChange(materialId: string): Promise<void>;
  onAddonChange(addonId: string, selected: boolean): Promise<void>;
  onDateChange(date: string | null): Promise<void>;

  onArtworkModeChange(mode: ArtworkMode): Promise<ArtworkModeChangeResult>;
  onArtworkModeConfirm(mode: ArtworkMode): Promise<void>;
  onArtworkUpload(file: File): Promise<void>;
  onCancelArtworkUpload(): void;
  onArtworkRemove(): Promise<void>;
  onCreateUploadLink(): Promise<void>;
  onCheckUploadSession(): Promise<void>;

  replaceSchema(schema: ConfiguratorSchema): Promise<void>;
  retryPricing(): Promise<PricingQuoteOutcome>;

  submit(): Promise<SubmitOutcome>;
  /** Explicitly acknowledges the currently displayed replacement quote after PRICE_CHANGED. */
  acknowledgePriceChange(): boolean;
  resolveUnknownOrderOutcome(): Promise<void>;
  retryUnknownOrderOutcome(): Promise<void>;
  recoverCartSession(): Promise<CartSessionRecoveryOutcome>;
  resolveCartOwnership(choice: "guest-cart" | "account-cart"): Promise<void>;

  flushDraft(): void;
  dispose(): void;
}

const defaultScheduler: Scheduler = {
  setTimeout(cb, ms) { return globalThis.setTimeout(cb, ms); },
  clearTimeout(id) { globalThis.clearTimeout(id as ReturnType<typeof setTimeout>); },
};

const defaultClock: Clock = { now: () => Date.now() };

export function createProductPageController(options: {
  configurator: ConfiguratorController;
  pricing: PricingController;
  artwork: ArtworkController;
  orderLine: OrderLineController;
  draft: DraftController;
  schemaVersion: number;
  orderContext?: DraftOrderContext;
  scheduler?: Scheduler;
  clock?: Clock;
  dimensionDebounceMs?: number;
  quantityDebounceMs?: number;
  /** How long to wait before re-checking a mutation the server reports pending. */
  pendingRecheckMs?: number;
  maxPendingRechecks?: number;
}): ProductPageController {
  const { configurator, pricing, artwork, orderLine, draft } = options;
  const scheduler = options.scheduler ?? defaultScheduler;
  const clock = options.clock ?? defaultClock;
  const dimensionDebounceMs = options.dimensionDebounceMs ?? 300;
  const quantityDebounceMs = options.quantityDebounceMs ?? 300;
  const pendingRecheckMs = options.pendingRecheckMs ?? 3_000;
  const maxPendingRechecks = options.maxPendingRechecks ?? 5;

  let currentSchemaVersion = options.schemaVersion;
  let orderContext: DraftOrderContext = options.orderContext ?? { type: "create" };
  let disposed = false;

  const timers: Record<DebouncedField, unknown | null> = { widthCm: null, heightCm: null, quantity: null };
  const committedRevision: Record<DebouncedField, number | null> = { widthCm: null, heightCm: null, quantity: null };

  let pendingRecheckTimer: unknown | null = null;
  let pendingRecheckCount = 0;
  /** Set while a submit is in flight so a second click cannot start another. */
  let submitInFlight = false;
  /** PRICE_CHANGED is a two-step flow: requote first, explicit customer acknowledgement second. */
  let priceChangeConfirmationRequired = false;
  let acknowledgedPriceQuoteKey: string | null = null;

  /* ---------- timers ---------- */

  function clearTimer(field: DebouncedField) {
    const id = timers[field];
    if (id !== null) { scheduler.clearTimeout(id); timers[field] = null; }
  }

  function cancelDebounces() {
    (Object.keys(timers) as DebouncedField[]).forEach(clearTimer);
  }

  function clearPendingRecheck() {
    if (pendingRecheckTimer !== null) { scheduler.clearTimeout(pendingRecheckTimer); pendingRecheckTimer = null; }
  }

  /* ---------- draft ---------- */

  function draftSnapshot(): PvcDraftSourceSnapshot {
    return {
      productSchemaVersion: currentSchemaVersion,
      configurator: configurator.getDraftSnapshot(),
      artwork: artwork.getDraftSnapshot(),
      orderContext,
    };
  }

  function touchDraft() {
    if (!disposed) draft.markDirty();
  }

  /* ---------- assessment target ---------- */

  /**
   * Artwork warnings depend on the printed size, so every accepted
   * configuration re-targets the assessment. An invalid configuration clears
   * the target rather than assessing against stale dimensions.
   */
  function syncAssessmentTarget(configuration: PvcConfiguration | null) {
    const target: ArtworkTarget | null =
      configuration ? { widthCm: configuration.widthCm, heightCm: configuration.heightCm } : null;
    artwork.setAssessmentTarget(target);
  }

  /* ---------- pricing ---------- */

  async function requestQuote(configuration: PvcConfiguration): Promise<void> {
    const outcome = await pricing.quote(configuration, currentSchemaVersion);
    if (disposed) return;
    if (outcome.type === "rejected") configurator.applyServerRejection(outcome.rejection);
  }

  async function handleEffect(effect: ConfiguratorEffect): Promise<void> {
    if (disposed) return;

    switch (effect.type) {
      case "NONE":
        return;

      case "PRICING_DIRTY":
        clearPriceChangeAcknowledgement();
        pricing.invalidate("CONFIG_CHANGED");
        touchDraft();
        return;

      case "PRICING_INVALID":
        clearPriceChangeAcknowledgement();
        pricing.invalidate("CONFIG_INVALID");
        syncAssessmentTarget(null);
        touchDraft();
        return;

      case "PRICING_READY":
        clearPriceChangeAcknowledgement();
        syncAssessmentTarget(effect.configuration);
        touchDraft();
        await requestQuote(effect.configuration);
        return;
    }
  }

  /* ---------- field commits ---------- */

  function currentRevision(field: DebouncedField): number {
    return field === "quantity"
      ? configurator.getQuantityEditRevision()
      : configurator.getDimensionEditRevision(field);
  }

  function commitField(field: DebouncedField, editRevision?: number): ConfiguratorEffect {
    if (editRevision !== undefined && committedRevision[field] === editRevision) {
      return { type: "NONE" };
    }
    const effect = field === "quantity"
      ? configurator.commitQuantity(editRevision)
      : configurator.commitDimension(field, editRevision);
    if (effect.type !== "NONE") committedRevision[field] = editRevision ?? currentRevision(field);
    return effect;
  }

  function scheduleCommit(field: DebouncedField, delayMs: number) {
    clearTimer(field);
    const revision = currentRevision(field);
    timers[field] = scheduler.setTimeout(() => {
      timers[field] = null;
      if (disposed) return;
      void handleEffect(commitField(field, revision));
    }, delayMs);
  }

  async function blurField(field: DebouncedField): Promise<void> {
    clearTimer(field);
    await handleEffect(commitField(field, currentRevision(field)));
  }

  /* ---------- submit ---------- */

  function readyQuote(): { configurationHash: string; pricingVersion: string } | null {
    const state = pricing.getState();
    return state.status === "ready"
      ? { configurationHash: state.quote.configurationHash, pricingVersion: state.quote.pricingVersion }
      : null;
  }

  function ownershipConflictOpen(): boolean {
    const state = orderLine.getState();
    return state.status === "conflict" && state.conflict.code === "CART_OWNERSHIP_CONFLICT";
  }

  function currentReadyQuoteKey(configuration: PvcConfiguration): string | null {
    if (!pricing.isCurrentQuoteValid(configuration)) return null;
    const state = pricing.getState();
    if (state.status !== "ready") return null;
    return `${state.quote.configurationHash}:${state.quote.pricingVersion}`;
  }

  function clearPriceChangeAcknowledgement() {
    acknowledgedPriceQuoteKey = null;
  }

  function acknowledgePriceChange(): boolean {
    if (disposed || !priceChangeConfirmationRequired) return false;
    const configuration = configurator.getPricingConfiguration();
    if (!configuration) return false;
    const key = currentReadyQuoteKey(configuration);
    if (!key) return false;
    acknowledgedPriceQuoteKey = key;
    return true;
  }

  function schedulePendingRecheck() {
    clearPendingRecheck();
    if (pendingRecheckCount >= maxPendingRechecks) return;

    pendingRecheckTimer = scheduler.setTimeout(() => {
      pendingRecheckTimer = null;
      if (disposed) return;
      pendingRecheckCount++;
      void orderLine.resolveUnknownOutcome().then(async outcome => {
        if (disposed) return;
        await handleRecoveredOrderOutcome(outcome);
      });
    }, pendingRecheckMs);
  }

  async function handleRecoveredOrderOutcome(
    outcome: Awaited<ReturnType<OrderLineController["resolveUnknownOutcome"]>>
  ): Promise<void> {
    if (disposed) return;

    if (outcome.type === "pending") {
      schedulePendingRecheck();
      return;
    }

    if (outcome.type === "success") {
      pendingRecheckCount = 0;
      clearPendingRecheck();
      if (orderContext.type === "edit") {
        orderContext = {
          type: "edit",
          orderLineId: outcome.result.orderLineId,
          orderLineVersion: outcome.result.orderLineVersion,
        };
      }
      draft.clear();
      return;
    }

    if (outcome.type === "conflict") {
      await routeConflict(outcome.conflict);
    }
  }

  async function submit(): Promise<SubmitOutcome> {
    if (disposed) return { type: "blocked", reason: { kind: "busy" } };
    if (submitInFlight) return { type: "blocked", reason: { kind: "busy" } };

    // An unanswered cart-ownership question must be settled by the customer
    // before anything is written into either cart.
    if (ownershipConflictOpen()) return { type: "blocked", reason: { kind: "cart-ownership" } };

    submitInFlight = true;
    try {
      cancelDebounces();

      const validation = configurator.validateForSubmit();
      if (!validation.valid) {
        return { type: "blocked", reason: { kind: "configuration", errorFields: validation.errorFields } };
      }
      const configuration = validation.configuration;

      const readiness = artwork.getOrderReadiness();
      if (!readiness.ready) {
        return { type: "blocked", reason: { kind: "artwork", reason: readiness.reason } };
      }

      // Never submit on a quote that does not belong to this exact configuration.
      if (!pricing.isCurrentQuoteValid(configuration)) {
        await requestQuote(configuration);
        if (disposed) return { type: "blocked", reason: { kind: "busy" } };
        if (!pricing.isCurrentQuoteValid(configuration)) {
          return { type: "blocked", reason: { kind: "quote", reason: "STALE" } };
        }
      }

      const quote = readyQuote();
      if (!quote) return { type: "blocked", reason: { kind: "quote", reason: "NOT_READY" } };

      // PRICE_CHANGED always requires a distinct, explicit confirmation of the
      // replacement quote. Merely clicking submit again must never count as
      // acknowledgement. The acknowledgement is bound to this exact quote.
      if (priceChangeConfirmationRequired) {
        const currentKey = currentReadyQuoteKey(configuration);
        if (!currentKey || acknowledgedPriceQuoteKey !== currentKey) {
          return { type: "blocked", reason: { kind: "price-changed" } };
        }
        // The customer has explicitly confirmed this quote. If the server
        // returns PRICE_CHANGED again, routeConflict will arm a fresh gate.
        priceChangeConfirmationRequired = false;
        acknowledgedPriceQuoteKey = null;
      }

      const submission: PvcOrderSubmission = {
        configuration,
        schemaVersion: currentSchemaVersion,
        artwork: readiness.selection,
        quote,
      };

      const outcome = orderContext.type === "edit"
        ? await orderLine.update(submission, {
            orderLineId: orderContext.orderLineId,
            expectedVersion: orderContext.orderLineVersion,
          })
        : await orderLine.add(submission);

      if (disposed) return { type: "blocked", reason: { kind: "busy" } };

      switch (outcome.type) {
        case "success": {
          priceChangeConfirmationRequired = false;
          acknowledgedPriceQuoteKey = null;
          pendingRecheckCount = 0;
          clearPendingRecheck();
          if (orderContext.type === "edit") {
            orderContext = {
              type: "edit",
              orderLineId: outcome.result.orderLineId,
              orderLineVersion: outcome.result.orderLineVersion,
            };
          }
          // The configuration now lives on the server; the local draft is spent.
          draft.clear();
          return {
            type: "success",
            orderLineId: outcome.result.orderLineId,
            orderLineVersion: outcome.result.orderLineVersion,
            cart: outcome.result.cart,
          };
        }

        case "conflict":
          await routeConflict(outcome.conflict);
          return { type: "conflict", conflict: outcome.conflict };

        case "pending":
          pendingRecheckCount = 0;
          schedulePendingRecheck();
          return { type: "pending", clientMutationId: outcome.clientMutationId };

        case "unknown-outcome":
          pendingRecheckCount = 0;
          schedulePendingRecheck();
          return { type: "unknown-outcome", clientMutationId: outcome.clientMutationId };

        case "failed":
          return { type: "failed", failure: outcome.failure };

        default:
          return { type: "blocked", reason: { kind: "busy" } };
      }
    } finally {
      submitInFlight = false;
    }
  }

  /**
   * Conflicts are surfaced, never auto-resolved. Field-level ones are pushed
   * back into the configurator so the existing focus rules apply.
   */
  async function routeConflict(conflict: OrderMutationConflict): Promise<void> {
    switch (conflict.code) {
      case "CONFIGURATION_INVALID":
        configurator.applyServerRejection({
          code: "CONFIGURATION_INVALID",
          fieldErrors: conflict.fieldErrors,
          invalidOptionIds: [],
          earliestAvailableDate: null,
          currentSchemaVersion: null,
        });
        return;

      case "OPTION_UNAVAILABLE":
        configurator.applyServerRejection({
          code: "OPTION_UNAVAILABLE",
          fieldErrors: [],
          invalidOptionIds: conflict.invalidOptionIds,
          earliestAvailableDate: null,
          currentSchemaVersion: null,
        });
        return;

      case "DATE_UNAVAILABLE":
        configurator.applyServerRejection({
          code: "DATE_UNAVAILABLE",
          fieldErrors: [{ field: "requestedDate", code: "DATE_UNAVAILABLE" }],
          invalidOptionIds: [],
          earliestAvailableDate: conflict.earliestAvailableDate,
          currentSchemaVersion: null,
        });
        return;

      case "PRICE_CHANGED": {
        // Refresh the visible quote now, but arm an explicit acknowledgement
        // gate. A second raw submit is blocked even after the requote is ready.
        priceChangeConfirmationRequired = true;
        acknowledgedPriceQuoteKey = null;
        pricing.invalidate("SESSION_CHANGED");
        const configuration = configurator.getPricingConfiguration();
        if (configuration) await requestQuote(configuration);
        return;
      }

      case "ARTWORK_FILE_INVALID":
        artwork.invalidateAcceptedFile();
        touchDraft();
        return;

      case "PRODUCT_SCHEMA_CHANGED":
      case "CART_SESSION_EXPIRED":
      case "CART_OWNERSHIP_CONFLICT":
      case "ORDER_LINE_VERSION_CONFLICT":
        return;
    }
  }

  /* ---------- init / restore ---------- */

  async function init(): Promise<InitOutcome> {
    if (disposed) return { draft: "none", removedAddonIds: [], materialInvalidated: false };

    const loaded = draft.load();
    if (loaded.type !== "restored") {
      const configuration = configurator.getPricingConfiguration();
      if (configuration) {
        syncAssessmentTarget(configuration);
        await requestQuote(configuration);
      }
      return { draft: loaded.type === "discarded" ? "discarded" : "none", removedAddonIds: [], materialInvalidated: false };
    }

    orderContext = loaded.draft.orderContext;
    const reconciliation = configurator.restoreDraft(loaded.draft.configurator);

    // Artwork verification runs in parallel: it is not a pricing dependency.
    const artworkRestore = artwork.restoreDraft(loaded.draft.artwork);

    if (reconciliation.effect.type === "PRICING_READY") {
      syncAssessmentTarget(reconciliation.effect.configuration);
      await requestQuote(reconciliation.effect.configuration);
    } else {
      pricing.invalidate("CONFIG_INVALID");
      syncAssessmentTarget(null);
    }

    await artworkRestore;

    return {
      draft: "restored",
      removedAddonIds: reconciliation.removedAddonIds,
      materialInvalidated: reconciliation.materialInvalidated,
    };
  }

  /* ---------- public API ---------- */

  return {
    init,

    onDimensionInput(field, raw) {
      if (disposed) return;
      void handleEffect(configurator.setDimensionInput(field, raw));
      scheduleCommit(field, dimensionDebounceMs);
    },

    onDimensionBlur(field) { return blurField(field); },

    onQuantityInput(raw) {
      if (disposed) return;
      void handleEffect(configurator.setQuantityInput(raw));
      scheduleCommit("quantity", quantityDebounceMs);
    },

    onQuantityBlur() { return blurField("quantity"); },

    async onQuantityStep(delta) {
      if (disposed) return;
      clearTimer("quantity");
      await handleEffect(configurator.stepQuantity(delta));
    },

    async onMaterialChange(materialId) {
      if (disposed) return;
      await handleEffect(configurator.setMaterial(materialId));
    },

    async onAddonChange(addonId, selected) {
      if (disposed) return;
      await handleEffect(configurator.toggleAddon(addonId, selected));
    },

    async onDateChange(date) {
      if (disposed) return;
      await handleEffect(configurator.setRequestedDate(date));
    },

    async onArtworkModeChange(mode) {
      if (disposed) return { type: "rejected", code: "MODE_UNAVAILABLE" };
      const result = await artwork.setMode(mode);
      if (result.type === "changed") touchDraft();
      return result;
    },

    async onArtworkModeConfirm(mode) {
      if (disposed) return;
      await artwork.confirmModeChange(mode);
      touchDraft();
    },

    async onArtworkUpload(file) {
      if (disposed) return;
      await artwork.uploadNow(file);
      touchDraft();
    },

    onCancelArtworkUpload() {
      if (disposed) return;
      artwork.cancelDirectUpload();
      touchDraft();
    },

    async onArtworkRemove() {
      if (disposed) return;
      await artwork.removeAcceptedFile();
      touchDraft();
    },

    async onCreateUploadLink() {
      if (disposed) return;
      await artwork.createUploadLink();
      touchDraft();
    },

    async onCheckUploadSession() {
      if (disposed) return;
      await artwork.checkUploadSession();
      touchDraft();
    },

    async replaceSchema(schema) {
      if (disposed) return;
      cancelDebounces();
      clearPriceChangeAcknowledgement();
      pricing.invalidate("SCHEMA_CHANGED");

      const result = configurator.replaceSchema(schema);
      currentSchemaVersion = schema.schemaVersion;
      touchDraft();

      if (result.effect.type === "PRICING_READY") {
        syncAssessmentTarget(result.effect.configuration);
        await requestQuote(result.effect.configuration);
      } else if (result.effect.type === "PRICING_INVALID") {
        pricing.invalidate("CONFIG_INVALID");
        syncAssessmentTarget(null);
      }
    },

    retryPricing() { return pricing.retry(); },

    submit,
    acknowledgePriceChange,

    async resolveUnknownOrderOutcome() {
      if (disposed) return;
      const outcome = await orderLine.resolveUnknownOutcome();
      await handleRecoveredOrderOutcome(outcome);
    },

    async retryUnknownOrderOutcome() {
      if (disposed) return;
      const outcome = await orderLine.retryUnknownOutcome();
      await handleRecoveredOrderOutcome(outcome);
    },

    async recoverCartSession() {
      clearPriceChangeAcknowledgement();
      if (disposed) {
        return { type: "discarded", reason: "disposed" } as CartSessionRecoveryOutcome;
      }
      const outcome = await orderLine.recoverCartSession();
      if (outcome.type === "recovered") {
        pricing.invalidate("SESSION_CHANGED");
        const configuration = configurator.getPricingConfiguration();
        if (configuration) await requestQuote(configuration);
      }
      return outcome;
    },

    async resolveCartOwnership(choice) {
      clearPriceChangeAcknowledgement();
      if (disposed) return;
      const outcome = await orderLine.resolveCartOwnership(choice);
      if (outcome.type === "resolved") {
        pricing.invalidate("SESSION_CHANGED");
        const configuration = configurator.getPricingConfiguration();
        if (configuration) await requestQuote(configuration);
      }
    },

    flushDraft() { if (!disposed) draft.flush(); },

    dispose() {
      disposed = true;
      cancelDebounces();
      clearPendingRecheck();
      // Child controllers are disposed by whoever created them.
    },
  };
}

export type { PvcDraftSourceSnapshot };
export const __internals = { defaultClock };
